import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './ui';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    const msg = error?.message || String(error || '');
    const isChunkError =
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('error loading dynamically imported module') ||
      msg.includes('Loading chunk');

    if (isChunkError) {
      const lastReload = sessionStorage.getItem('z3_chunk_reload');
      const now = Date.now();
      if (!lastReload || now - Number(lastReload) > 15_000) {
        sessionStorage.setItem('z3_chunk_reload', String(now));
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      const msg = this.state.error?.message || '';
      const isChunkError =
        msg.includes('Failed to fetch dynamically imported module') ||
        msg.includes('Importing a module script failed') ||
        msg.includes('error loading dynamically imported module');

      return (
        <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-xl text-amber-700">
              ⚠️
            </div>
            <h2 className="text-lg font-semibold text-slate-900">
              {isChunkError ? 'New Update Available' : 'Something went wrong'}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              {isChunkError
                ? 'A new version of the app was published. Please refresh to load the latest screens.'
                : 'An unexpected error occurred while loading this page.'}
            </p>
            <div className="mt-5 flex justify-center gap-3">
              <Button
                variant="accent"
                onClick={() => {
                  sessionStorage.setItem('z3_chunk_reload', String(Date.now()));
                  window.location.reload();
                }}
              >
                Refresh page
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  window.location.href = '/admin';
                }}
              >
                Go to home
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
